import { useQuery } from "@tanstack/react-query"
import { TRPCClientError } from "@trpc/client"
import { trpc } from "#/lib/trpc/client"
import { trpcRetry } from "#/lib/trpc/retryPolicy"
import { useCurrentUser } from "#/components/context/user/UserContext"

export type FeatureAvailability = Awaited<ReturnType<typeof trpc.member.features.query>>

export type FeatureAvailabilityState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; features: FeatureAvailability }

const FEATURE_AVAILABILITY_QUERY_KEY = "GetFeatureAvailability"

// Config only, the same for every program; read it through useNavAvailability, which applies program rules.
// A failed refetch keeps the last answer, so only a member with no answer yet reads as "error".
export function useFeatureAvailability(): FeatureAvailabilityState {
  const { company } = useCurrentUser()
  const countryCode = company.countryCode
  const { data, isError } = useQuery<FeatureAvailability, TRPCClientError<never>>({
    queryKey: [FEATURE_AVAILABILITY_QUERY_KEY, countryCode],
    queryFn: () => trpc.member.features.query({ countryCode }),
    refetchOnWindowFocus: false,
    retry: trpcRetry(1),
    // Reported once here rather than by each of its many readers.
    meta: {
      errorReport: {
        message: "member.features query failed",
        tags: { query: "member.features" }
      }
    }
  })

  if (data) return { status: "ready", features: data }
  if (isError) return { status: "error" }
  return { status: "loading" }
}
